import Delete from '@mui/icons-material/Delete';
import {
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { ArrayPath, Control, FieldArray, FieldValues, Path } from 'react-hook-form';
import { Controller, useFieldArray } from 'react-hook-form';

import CurrencySymbol from '~/components/common/CurrencySymbol';
import type { FieldDef } from '~/components/form/utils';
import { BUTTON_SX } from '~/theme';

interface ILineItemsProps<T extends FieldValues> {
  control: Control<T>;
  fieldDef: FieldDef;
  errorMessage?: string;
}

const LineItems = <T extends FieldValues>({
  control,
  fieldDef,
  errorMessage,
}: ILineItemsProps<T>) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldDef.name as ArrayPath<T>,
  });

  const handleAddRow = () => {
    append({ description: '', amount: 0 } as FieldArray<T, ArrayPath<T>>);
  };

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((item, index) => (
            <TableRow key={item.id}>
              <TableCell>
                <Controller
                  name={`${fieldDef.name}.${index}.description` as Path<T>}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      error={typeof error !== 'undefined'}
                      helperText={error?.message}
                      fullWidth
                      size="small"
                      required
                      slotProps={{
                        htmlInput: {
                          'aria-label': `Line Item ${index + 1} Description`,
                          role: 'textbox',
                        },
                      }}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Controller
                  name={`${fieldDef.name}.${index}.amount` as Path<T>}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value ?? ''}
                      onBlur={field.onBlur}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value != '' ? parseFloat(e.target.value) : e.target.value,
                        )
                      }
                      type="number"
                      slotProps={{
                        htmlInput: {
                          'aria-label': `Line Item ${index + 1} Amount`,
                          role: 'textbox',
                          step: 0.01,
                        },
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <CurrencySymbol />
                            </InputAdornment>
                          ),
                        },
                      }}
                      required
                      error={typeof error !== 'undefined'}
                      helperText={error?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <IconButton onClick={() => remove(index)}>
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div>
        <Typography variant="body2" color="error">
          {errorMessage ? errorMessage : null}
        </Typography>
      </div>
      <div>
        <Button onClick={handleAddRow} sx={BUTTON_SX}>
          + Add Row
        </Button>
      </div>
    </>
  );
};

export default LineItems;
